/**
 * client/settings/settings.ts — player options model, persisted in localStorage.
 *
 * OWNER: INPUT agent (contract shape by integration; add fields with defaults, bump VERSION
 * only for incompatible changes — migrate() keeps unknown/missing fields safe).
 * Sources: research/controller.md §2 (layouts), §4.1 (sensitivity 1-14, default 4), §5 (aim assist
 * profile vars input_targetassist / input_autoaim), §6 (stance), §7 (rumble, gpad_rumble), §8.3 (WebHID);
 * cg_fov 65 (models-anims.md §4); m_yaw/m_pitch 0.022 (research/data/dvars_t6mp.json).
 */
export type ButtonLayout = 'default' | 'tactical' | 'lefty' | 'nomad' | 'charlie';
export type StickLayout = 'default' | 'southpaw' | 'legacy' | 'legacysouthpaw';

export const BUTTON_LAYOUTS: readonly ButtonLayout[] = ['default', 'tactical', 'lefty', 'nomad', 'charlie'];
export const STICK_LAYOUTS_LIST: readonly StickLayout[] = ['default', 'southpaw', 'legacy', 'legacysouthpaw'];
/** BO2 menu labels (MENU_DEFAULT_CAPS, MENU_TACTICAL_CAPS, ...). */
export const BUTTON_LAYOUT_LABELS: Readonly<Record<ButtonLayout, string>> = {
  default: 'Default', tactical: 'Tactical', lefty: 'Lefty', nomad: 'Nomad', charlie: 'Charlie',
};
export const STICK_LAYOUT_LABELS: Readonly<Record<StickLayout, string>> = {
  default: 'Default', southpaw: 'Southpaw', legacy: 'Legacy', legacysouthpaw: 'Legacy Southpaw',
};

/** Keyboard/mouse actions (KeyboardEvent.code values, 'Mouse0'..'Mouse4', 'WheelUp'/'WheelDown'). */
export type KbAction =
  | 'forward' | 'back' | 'left' | 'right' | 'jump' | 'crouch' | 'prone' | 'sprint' | 'attack' | 'ads'
  | 'melee' | 'reload' | 'use' | 'lethal' | 'tactical' | 'switchWeapon' | 'scoreboard' | 'pause'
  | 'weapon1' | 'weapon2';

export const KB_ACTION_LABELS: Readonly<Record<KbAction, string>> = {
  forward: 'Forward', back: 'Back', left: 'Move left', right: 'Move right', jump: 'Jump / stand up', crouch: 'Crouch',
  prone: 'Prone', sprint: 'Sprint / hold breath', attack: 'Fire', ads: 'Aim down sight', melee: 'Melee', reload: 'Reload',
  use: 'Use', lethal: 'Lethal (tomahawk)', tactical: 'Tactical', switchWeapon: 'Switch weapon', scoreboard: 'Scoreboard',
  pause: 'Pause', weapon1: 'Primary (crossbow)', weapon2: 'Secondary (ballistic knife)',
};

export interface Settings {
  version: number;
  name: string;
  // ---- controller -----------------------------------------------------------------------
  /** Look sensitivity slot 1..14 (VIEW_SENSITIVITY_TABLE), default 4 = 1.0. */
  lookSensitivity: number;
  invertPitch: boolean;
  buttonLayout: ButtonLayout;
  triggersFlipped: boolean;
  stickLayout: StickLayout;
  /** "Target assist" (slowdown + lock-on) [CTL §5]. */
  aimAssist: boolean;
  /** "Autoaim when aiming down the sight" [CTL §5.4]. */
  adsAutoAim: boolean;
  /**
   * Rotational lock-on while target assist is on. The PC exe registers aim_lockon_enabled 0; the
   * console behaviour (tracking strafing targets) is ESTIMATE on [CTL §5.3].
   */
  aimLockOn: boolean;
  rumble: boolean;
  /** Use the alternative response curve reading (debug A/B, CTL §4.3 caveat). */
  altStickCurve: boolean;
  /** Opt-in DualSense rumble over WebHID (Chrome/Edge desktop, needed on Windows) [CTL §8.3]. */
  webHidRumble: boolean;
  // ---- mouse / keyboard -----------------------------------------------------------------
  /** Degrees per mouse count at hip (m_yaw/m_pitch 0.022 * sensitivity). */
  mouseSensitivity: number;
  /** ADS multiplier applied on top of the FOV ratio (BO2 PC "ADS sensitivity ratio"). */
  mouseAdsScale: number;
  /** Pointer lock with unadjustedMovement (raw input, no OS acceleration) where supported. */
  mouseRawInput: boolean;
  keybinds: Record<KbAction, string[]>;
  /** Crouch/prone keys toggle (true) or hold (false). */
  toggleCrouch: boolean;
  toggleAds: boolean;
  // ---- view ---------------------------------------------------------------------------
  /** Horizontal FOV at 4:3 like cg_fov (65 = BO2 console). */
  fov: number;
  // ---- audio --------------------------------------------------------------------------
  volume: { master: number; sfx: number; music: number; voice: number };
  // ---- graphics / debug ----------------------------------------------------------------
  renderScale: number;
  shadows: boolean;
  showDebugOverlay: boolean;
  /** cl_timeNudge, ms (-30..30). */
  timeNudge: number;
}

export const SETTINGS_VERSION = 1;
const STORAGE_KEY = 'bo2sns.settings';

/**
 * BO2 PC-like defaults. ControlLeft is NOT bound: Ctrl+W (crouch + forward) closes the browser tab and
 * cannot be intercepted outside fullscreen keyboard lock.
 */
export const DEFAULT_KEYBINDS: Record<KbAction, string[]> = {
  forward: ['KeyW'], back: ['KeyS'], left: ['KeyA'], right: ['KeyD'],
  jump: ['Space'], crouch: ['KeyC'], prone: ['KeyZ'], sprint: ['ShiftLeft'],
  attack: ['Mouse0'], ads: ['Mouse2'], melee: ['KeyV', 'KeyE'], reload: ['KeyR'], use: ['KeyF'],
  lethal: ['KeyQ', 'KeyG'], tactical: ['Mouse4'], switchWeapon: ['WheelUp', 'WheelDown', 'Digit3'],
  scoreboard: ['Tab'], pause: ['Escape'], weapon1: ['Digit1'], weapon2: ['Digit2'],
};

export function defaultSettings(): Settings {
  return {
    version: SETTINGS_VERSION,
    name: 'Player',
    lookSensitivity: 4,
    invertPitch: false,
    buttonLayout: 'default',
    triggersFlipped: false,
    stickLayout: 'default',
    aimAssist: true,
    adsAutoAim: true,
    aimLockOn: true,
    rumble: true,
    altStickCurve: false,
    webHidRumble: false,
    mouseSensitivity: 0.022 * 3,
    mouseAdsScale: 1,
    mouseRawInput: true,
    keybinds: structuredClone(DEFAULT_KEYBINDS),
    toggleCrouch: true,
    toggleAds: false,
    fov: 65,
    volume: { master: 0.8, sfx: 1, music: 0.6, voice: 1 },
    renderScale: 1,
    shadows: true,
    showDebugOverlay: false, // a clean BO2 screen by default; opt in from Options or ?debug=1 (integration fix)
    timeNudge: 0,
  };
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

/** Merge stored data over defaults (unknown keys dropped, missing keys defaulted). */
export function migrateSettings(raw: unknown): Settings {
  const d = defaultSettings();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as Partial<Settings>;
  const out: Settings = { ...d };
  for (const k of Object.keys(d) as (keyof Settings)[]) {
    if (k in r && typeof r[k] === typeof d[k]) (out as unknown as Record<string, unknown>)[k] = r[k];
  }
  out.volume = { ...d.volume, ...(r.volume && typeof r.volume === 'object' ? r.volume : {}) };
  const kb = { ...d.keybinds };
  if (r.keybinds && typeof r.keybinds === 'object') {
    for (const k of Object.keys(d.keybinds) as KbAction[]) {
      const v = (r.keybinds as Record<string, unknown>)[k];
      if (isStringArray(v)) kb[k] = v.slice();
    }
  }
  out.keybinds = kb;
  if (!(['default', 'tactical', 'lefty', 'nomad', 'charlie'] as string[]).includes(out.buttonLayout)) out.buttonLayout = d.buttonLayout;
  if (!(['default', 'southpaw', 'legacy', 'legacysouthpaw'] as string[]).includes(out.stickLayout)) out.stickLayout = d.stickLayout;
  out.lookSensitivity = Math.min(14, Math.max(1, Math.round(Number.isFinite(out.lookSensitivity) ? out.lookSensitivity : 4)));
  out.timeNudge = Math.min(30, Math.max(-30, Number.isFinite(out.timeNudge) ? out.timeNudge : 0));
  if (!Number.isFinite(out.mouseSensitivity) || out.mouseSensitivity <= 0) out.mouseSensitivity = d.mouseSensitivity;
  if (!Number.isFinite(out.mouseAdsScale) || out.mouseAdsScale <= 0) out.mouseAdsScale = d.mouseAdsScale;
  if (!Number.isFinite(out.fov) || out.fov < 40 || out.fov > 120) out.fov = d.fov;
  out.version = SETTINGS_VERSION;
  return out;
}

export function loadSettings(): Settings {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return migrateSettings(s ? JSON.parse(s) : null);
  } catch {
    return defaultSettings();
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota: settings just won't persist */
  }
}
